import isEqual from 'lodash/isEqual.js'

import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
} from '../../../../store.js'
import {
  navigateTransferList,
  updateSearchText,
  updateTransferSendingWarehouseIDs,
  updateTransferReceivingWarehouseIDs,
  updateTransferDateRangeFilters,
  cleanTransferQuery,
} from '../../transferListFunctions.js'
import deferPromise from '../../../../common/deferPromise.js'
import {getTimeZone} from '../../../../common/date.js'
import {REPORT_INTERVAL_MONTH} from '../../../../common/constants/Reports.js'
import {
  isNonZeroPositiveInteger,
  isNonZeroPositiveNumeric,
} from '../../../../common/utils.js'
import {TRANSFER_DATE_FILTERS_OPTIONS} from '../../../../common/constants/Transfers.js'

const MODAL_FORM = 'transfer_list_filter_modal'

export const DATE_RANGE_FILTER_PANEL = 'date_range'
export const SENDING_WAREHOUSE_FILTER_PANEL = 'sending_warehouse'
export const RECEIVING_WAREHOUSE_FILTER_PANEL = 'receiving_warehouse'
export const MISC_FILTER_PANEL = 'misc'
export const TRANSFER_LIST_FILTER_OPTIONS = [
  {value: DATE_RANGE_FILTER_PANEL, display: 'Date Range'},
  {value: SENDING_WAREHOUSE_FILTER_PANEL, display: 'Sending Warehouses'},
  {value: RECEIVING_WAREHOUSE_FILTER_PANEL, display: 'Receiving Warehouses'},
  {value: MISC_FILTER_PANEL, display: 'Misc'},
]

export async function showTransferListFilterModal({query = {}} = {}) {
  const {updates} = await showTransferListFilterModalPrime({
    query,
    singleRun: true,
  })

  return await navigateTransferList(updates)
}

export async function showTransferQueryFilterModal({
  query = {},
  singleRun = true,
} = {}) {
  const {originalForm, updates} = await showTransferListFilterModalPrime({
    query,
    singleRun,
  })

  if (!updates) {
    return query
  }

  const transferQuery = cleanTransferQuery({...originalForm, ...updates})

  delete transferQuery.sort
  delete transferQuery.limit
  delete transferQuery.offset

  return transferQuery
}

export async function showTransferListFilterModalPrime({
  query = {},
  singleRun = true,
  deferredPromise = deferPromise(),
  originalForm,
} = {}) {
  const {
    date_type: dateFilterType,
    start_date: startDate,
    end_date: endDate,
    interval_type: intervalType,
    interval_amount: intervalAmount,
    interval_trunc: intervalTrunc,
  } = query

  const form = {
    search: query.search || '',
    sending_warehouse_id: query.sending_warehouse_id || [],
    receiving_warehouse_id: query.receiving_warehouse_id || [],
    dateFilterType,
    startDate,
    endDate,
    intervalType,
    intervalAmount,
    intervalTrunc,
    timezone: getTimeZone(),
    includeTimezone: !!query.timezone,
  }

  setForm(MODAL_FORM, {
    singleRun,
    deferredPromise,
    originalForm: originalForm || {...form},
    ...form,
  })

  return deferredPromise.promise
}

export function updateModalForm(...args) {
  updateForm(MODAL_FORM, ...args)
}

export function closeModal(updates) {
  const {originalForm, deferredPromise} = modalFormSelector(getState())

  deferredPromise.resolve({originalForm, updates})

  removeForm(MODAL_FORM)
}

export function clearFilters() {
  const {singleRun, deferredPromise, originalForm} =
    modalFormSelector(getState())

  // re-setup modal form with most of the pieces reused
  // reuse deferredPromise so that any awaits from the original call to show... will resolve
  // reuse originalForm so applyFilter() has the correct thing to compare against
  // reuse singleRun because that is part of the context
  showTransferListFilterModalPrime({
    singleRun,
    deferredPromise,
    originalForm,
  })
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

export function errorsSelector(state) {
  const {intervalType, intervalAmount} = modalFormSelector(state)
  const errors = {}

  if (intervalType) {
    if (
      intervalType === REPORT_INTERVAL_MONTH &&
      !isNonZeroPositiveInteger(intervalAmount)
    ) {
      errors.intervalAmount = 'Amount must be a positive whole number'
      errors.preventSave = true
    } else if (
      intervalType !== REPORT_INTERVAL_MONTH &&
      !isNonZeroPositiveNumeric(intervalAmount)
    ) {
      errors.intervalAmount = 'Amount must be a positive number'
      errors.preventSave = true
    }
  }

  return errors
}

export function modalValidDateRangeFilterTypesOptionsSelector() {
  return TRANSFER_DATE_FILTERS_OPTIONS
}

export async function applyFilter() {
  const {
    originalForm,
    search,
    sending_warehouse_id,
    receiving_warehouse_id,
    dateFilterType: date_type,
    startDate: start_date,
    endDate: end_date,
    intervalType: interval_type,
    intervalAmount: interval_amount,
    intervalTrunc: interval_trunc,
    timezone,
    includeTimezone,
  } = modalFormSelector(getState())

  let updates = {}

  if (search !== originalForm.search) {
    updates = {...updates, ...updateSearchText(search)}
  }

  if (!isEqual(sending_warehouse_id, originalForm.sending_warehouse_id)) {
    updates = {
      ...updates,
      ...updateTransferSendingWarehouseIDs(sending_warehouse_id),
    }
  }

  if (!isEqual(receiving_warehouse_id, originalForm.receiving_warehouse_id)) {
    updates = {
      ...updates,
      ...updateTransferReceivingWarehouseIDs(receiving_warehouse_id),
    }
  }

  if (
    date_type !== originalForm.dateFilterType ||
    start_date !== originalForm.startDate ||
    end_date !== originalForm.endDate ||
    interval_type !== originalForm.intervalType ||
    interval_amount !== originalForm.intervalAmount ||
    interval_trunc !== originalForm.intervalTrunc ||
    timezone !== originalForm.timezone ||
    includeTimezone !== originalForm.includeTimezone
  ) {
    updates = {
      ...updates,
      ...updateTransferDateRangeFilters(
        {
          date_type,
          start_date,
          end_date,
          interval_type,
          interval_amount,
          interval_trunc,
          timezone: includeTimezone ? timezone : undefined,
        },
        updates.status || originalForm.status,
      ),
      includeTimezone,
    }
  }

  closeModal(updates)
}
